// src/store.js
//
// Nothing here is persisted. Sign-in lives in memory only, so a refresh, a
// closed tab or a restart lands everyone back on the login screen — which is
// what a shared till in a restaurant should do.
import { configureStore, combineReducers } from "@reduxjs/toolkit";

// existing slices
import loginSlice, { logout, sessionEnded } from "./SignupAndLogin/loginSlice";
import userSlice from "./containers/user/userSlice";
import customerReducer from "./containers/customer/customerSlice";
import printerReducer from "./containers/printing/printerSlice";

// order page slices
import productReducer from "./containers/orders/productSlice";
import orderReducer from "./containers/orders/orderSlice";
import categoryReducer from "./containers/orders/categorySlice";
import orderListReducer from "./containers/orders/orderListSlice";

// orders screen — owns its own state so the POS cart and the dashboard,
// which both still read `orderList`, are untouched by it.
import ordersScreenReducer from "./containers/orders/ordersScreenSlice";

// expenses — the rows, their categories and the vendors an invoice is billed to
import expenseReducer from "./containers/expenses/expenseSlice";

// the vendor book — who the shop owes, and the ledger behind each balance
import vendorReducer from "./containers/vendor/vendorSlice";

// the store room — what is on the shelf, and the documents that moved it
import stockReducer from "./containers/stock/stockSlice";

// the monthly software bill — the plans, and whether the dialog is up
import billingReducer from "./containers/billing/billingSlice";

const appReducer = combineReducers({
  // existing
  login: loginSlice,
  users: userSlice,
  customers: customerReducer,

  // Station printers: Administration manages them, the order pad routes
  // kitchen tickets with them.
  printers: printerReducer,

  // order page (IMPORTANT: keys must match useSelector)
  products: productReducer,
  order: orderReducer,
  categories: categoryReducer,
  orderList: orderListReducer,

  // orders screen
  ordersScreen: ordersScreenReducer,

  // Money going out. Its date range lives here so a trip to another tab and
  // back returns to the same period.
  expenses: expenseReducer,

  // Money owed out. Separate from the expenses slice's vendor list on
  // purpose: that one is a picker, this one is a book with balances.
  vendors: vendorReducer,

  // The store room. Two lists — the shelf and the documents — because a write
  // invalidates both and a date range only invalidates one.
  stock: stockReducer,

  // The monthly bill. Whether money is OWED is not in here — that is
  // `business.notify`, which arrives with the login reply and lives in the
  // login slice. This is only what this session has fetched and opened.
  billing: billingReducer,
});

/**
 * Signing out empties everything.
 *
 * This exists because of a real bug: logging out of one business and into
 * another left the previous business's menu, categories, orders, customers and
 * half-built cart on screen. Each slice has its own `isXDataFetched` flag, and
 * a flag left at `true` means the new business's data is never fetched at all
 * — so the wrong menu doesn't just linger, it sticks.
 *
 * Clearing it here rather than dispatching a reset from every screen means a
 * slice added next month is covered without anyone remembering to. The login
 * slice is passed through untouched so it can handle its own action and keep
 * the notice explaining WHY the session ended ("Order #92 printed", "your
 * sign-in is no longer valid") — that message has to outlive the wipe.
 */
const rootReducer = (state, action) => {
  if (action.type === logout.type || action.type === sessionEnded.type) {
    return appReducer({ login: state?.login }, action);
  }
  return appReducer(state, action);
};

const store = configureStore({
  reducer: rootReducer,
});

export default store;
